import React, { useRef, useState } from 'react';
import { FormikConfig, useFormik } from 'formik';
import * as Yup from 'yup';
import { Typography, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import {
  ClickableBox,
  SecondaryTextA,
  SecondaryTextB,
  TextRow,
  SFieldContainer,
  SLabel,
  SStyledTextField,
  SFormContainer,
} from './styles';
import Text from '../Texts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { useUpdateIdentityData } from '../../hooks/useUpdateIdentityData.';
import { submitIdentityForm } from '../../thunks/submitIdentityForm';
import { useNavigate } from 'react-router-dom';
import {
  setCurrentStep,
  setNextStep,
  updateIdentityField,
} from '../../Slice/unifiedFormSlice';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import SubmitButton from '../Button/SubmitButton';
import { ArrowCircleLeft } from '@mui/icons-material';
import { ACTION_BLUE } from '../../theme/theme';

interface IdentityFormBoxProps {
  primary: string;
  secondary: string;
  backgroundColor: boolean;
  identityType: 'LEGAL' | 'PERSONAL';
}

interface FormValues {
  name: string;
  display_name: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required('نام الزامی است'),
  display_name: Yup.string().required('نام نمایشی الزامی است'),
});

const IdentityFormBox: React.FC<IdentityFormBoxProps> = ({
  primary,
  secondary,
  backgroundColor,
  identityType,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { identity } = useSelector((state: RootState) => state.unifiedForm);
  const { updateIdentityData, error: updateError } = useUpdateIdentityData();
  const [localError, setLocalError] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // تعیین متن tooltip بر اساس نوع هویت
  const tooltipText =
    identityType === 'PERSONAL'
      ? 'ثبت‌نام حقیقی مخصوص افراد است و اطلاعات با کارت ملی شخص احراز می‌شود.'
      : 'ثبت‌نام حقوقی مخصوص شرکت‌ها یا سازمان‌هاست و نیاز به مدارک رسمی شرکت دارد.';

  // پخش صدای notification
  const playNotificationSound = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
    playNotificationSound();
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  // استفاده از hook برای مدیریت submit
  // onSuccess takes no arguments and closes over the render that started the
  // submit, where `identity` is still the pre-reset value. Carry the server's
  // response in a ref so the navigate below sends the real new id — sending a
  // stale/empty one landed the wizard on "شناسه ناشر یافت نشد".
  const createdIdentity = useRef<{
    id: number | string;
    display_name: string;
    identity_type: string;
    step: number;
  } | null>(null);

  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات هویت با موفقیت ثبت شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: () => {
      const created = createdIdentity.current;
      if (!created) return;

      // تاخیر قبل از navigate
      setTimeout(() => {
        navigate('/wizard', {
          state: {
            id: created.id,
            display_name: created.display_name,
            identity_type: created.identity_type,
            nextStep: created.step,
            mode: 'new',
          },
        });
      }, 800);
    },
  });

  const handleFormSubmit: FormikConfig<FormValues>['onSubmit'] = async (
    values,
  ) => {
    await handleSubmit(async () => {
      const response = await dispatch(
        submitIdentityForm({
          identity_type: identityType,
          name: values.name,
          display_name: values.display_name,
        }),
      ).unwrap();

      if (!response.data.id) {
        throw new Error('شناسه هویت در پاسخ سرور یافت نشد');
      }

      const serverData = response.data;
      console.log('Full server response:', response);

      // ذخیره اطلاعات هویت در Redux
      dispatch(updateIdentityField({ field: 'id', value: serverData.id }));
      dispatch(
        updateIdentityField({
          field: 'display_name',
          value: serverData.display_name,
        }),
      );
      dispatch(
        updateIdentityField({
          field: 'type',
          value: serverData.identity_type,
        }),
      );

      // تنظیم nextStep و currentStep
      const nextStep = response['next-step'] || serverData.step || 2;
      dispatch(setNextStep(nextStep));
      dispatch(setCurrentStep(nextStep));

      createdIdentity.current = {
        id: serverData.id,
        display_name: serverData.display_name,
        identity_type: serverData.identity_type,
        step: nextStep,
      };

      // به‌روزرسانی هویت با استفاده از هوک
      updateIdentityData({
        id: serverData.id,
        name: serverData.name,
        display_name: serverData.display_name,
        slug: serverData.slug,
        status: serverData.status,
        active: serverData.active,
        content_status: serverData.content_status,
        identity_type: serverData.identity_type,
        step: serverData.step || response['current-step'] || 1,
        'next-step': nextStep,
      });

      console.log('After update, nextStep set to:', nextStep);

      setLocalError(null);

      return response;
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      name: identity.name || '',
      display_name: identity.display_name || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  return (
    <Tooltip
      title={tooltipText}
      placement="top"
      arrow
      open={tooltipOpen}
      onOpen={handleTooltipOpen}
      onClose={handleTooltipClose}
      TransitionProps={{
        timeout: 300,
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#FFFFFF',
            color: '#4994ec',
            fontSize: '14px',
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 500,
            lineHeight: 1.8,
            padding: '12px 16px',
            borderRadius: '8px',
            textAlign: 'right',
            direction: 'rtl',
            maxWidth: '300px',
            border: '1px solid #E5E5E8',
            boxShadow: '0 4px 16px rgba(16, 24, 40, 0.12)',
          },
        },
        arrow: {
          sx: {
            color: '#FFFFFF',
            // Without this the arrow reads as a floating white triangle,
            // detached from the tooltip's border.
            '&::before': { border: '1px solid #E5E5E8' },
          },
        },
      }}
    >
      <ClickableBox>
        <TextRow>
          <SecondaryTextA>{primary}</SecondaryTextA>
          <SecondaryTextB>{secondary}</SecondaryTextB>
        </TextRow>

        <SFormContainer onSubmit={formik.handleSubmit}>
          <SFieldContainer>
            <SLabel>نام</SLabel>
            <SStyledTextField
              variant="outlined"
              placeholder="نام خود را وارد کنید"
              size="small"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
            />
          </SFieldContainer>

          <SFieldContainer>
            <SLabel>نام نمایشی</SLabel>
            <SStyledTextField
              variant="outlined"
              placeholder="نام نمایشی خود را وارد کنید"
              size="small"
              name="display_name"
              value={formik.values.display_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.display_name && !!formik.errors.display_name}
              helperText={
                formik.touched.display_name && formik.errors.display_name
              }
            />
          </SFieldContainer>

        {localError && (
          <SFieldContainer>
            <Typography color="error">{localError}</Typography>
          </SFieldContainer>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            direction: 'ltr',
            marginTop: '8px',
          }}
        >
          <SubmitButton
            type="submit"
            startIcon={<ArrowCircleLeft />}
            isSubmitting={isSubmitting}
            loadingText="در حال ارسال..."
            useDefaultStyle={false}
            sx={{
              minWidth: '100px',
              padding: '7px 15px',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 14,
              fontWeight: 500,
              borderRadius: '8px',
              backgroundColor: ACTION_BLUE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                backgroundColor: '#1565C0',
              },
              '&:disabled': {
                backgroundColor: '#b0bec5',
              },
              '& .MuiButton-startIcon': {
                margin: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            ادامه
          </SubmitButton>
        </div>
      </SFormContainer>
    </ClickableBox>
    </Tooltip>
  );
};

export default IdentityFormBox;
